import {
  collection, doc, getDocs, query, where, addDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { db, COLLECTIONS } from './config';

// Get teacher earnings summary
export const getTeacherEarnings = async (teacherId) => {
  try {
    const bookingsQuery = query(
      collection(db, COLLECTIONS.BOOKINGS),
      where('teacherId', '==', teacherId),
      where('status', '==', 'completed'),
      where('paymentStatus', '==', 'paid')
    );

    const snapshot = await getDocs(bookingsQuery);
    const bookings = snapshot.docs.map(d => d.data());

    const totalEarnings = bookings.reduce((sum, b) => sum + (b.price || 0), 0);
    const platformFee = totalEarnings * 0.15; // 15% platform fee
    const netEarnings = totalEarnings - platformFee;

    // Get withdrawal history
    const withdrawalsQuery = query(
      collection(db, COLLECTIONS.WITHDRAWALS),
      where('teacherId', '==', teacherId)
    );
    const withdrawalsSnap = await getDocs(withdrawalsQuery);
    const withdrawals = withdrawalsSnap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt
    }));

    const totalWithdrawn = withdrawals
      .filter(w => w.status === 'completed')
      .reduce((sum, w) => sum + w.amount, 0);

    const pendingWithdrawals = withdrawals
      .filter(w => w.status === 'pending')
      .reduce((sum, w) => sum + w.amount, 0);

    const availableBalance = netEarnings - totalWithdrawn - pendingWithdrawals;

    // Monthly breakdown
    const monthlyEarnings = {};
    bookings.forEach(b => {
      const month = b.date?.substring(0, 7); // YYYY-MM
      if (month) {
        monthlyEarnings[month] = (monthlyEarnings[month] || 0) + (b.price || 0);
      }
    });

    return {
      totalEarnings,
      platformFee,
      netEarnings,
      totalWithdrawn,
      pendingWithdrawals,
      availableBalance,
      totalLessons: bookings.length,
      monthlyEarnings,
      withdrawals: withdrawals.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    };
  } catch (error) {
    console.error('Error getting earnings:', error);
    throw error;
  }
};

// Request a withdrawal
export const requestWithdrawal = async (teacherId, amount, paypalEmail) => {
  try {
    const earnings = await getTeacherEarnings(teacherId);
    if (amount > earnings.availableBalance) {
      throw new Error('Insufficient balance');
    }
    if (amount < 10) {
      throw new Error('Minimum withdrawal is $10');
    }

    const withdrawalRef = await addDoc(collection(db, COLLECTIONS.WITHDRAWALS), {
      teacherId,
      amount,
      paypalEmail,
      status: 'pending', // pending, processing, completed, rejected
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { id: withdrawalRef.id };
  } catch (error) {
    console.error('Error requesting withdrawal:', error);
    throw error;
  }
};

// Admin: Process withdrawal
export const processWithdrawal = async (withdrawalId, status, adminNote = '') => {
  try {
    await updateDoc(doc(db, COLLECTIONS.WITHDRAWALS, withdrawalId), {
      status,
      adminNote,
      processedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error processing withdrawal:', error);
    throw error;
  }
};
